import { Typography, makeStyles, IconButton, Menu, MenuItem } from '@material-ui/core';
import ArrowForward from '@material-ui/icons/ArrowForward';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { IUpdate } from 'interface/updates';
import React, { Fragment } from 'react';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { dataToNotifications, getCdnUrl } from 'utils';
import { deleteUpdate, readAndUnreadUpdate } from 'redux/updateNotifications/updateNotificationsSlice';
import { useAppDispatch } from 'hooks/redux';

interface IProps {
  update: IUpdate;
}

const useStyles = makeStyles((theme) => {
  return {
    container: {
      display: 'flex',
      width: '100%',
      height: 105,
      marginBottom: 24,
    },
    imgCover: {
      width: 100,
      height: 100,
      border: `1px solid ${theme.defaultTheme.gray_palette.light2}`,
      marginRight: 14,
      cursor: 'pointer',
      '@media (max-width: 600px)': {
        display: 'none',
      },
      '& > img': {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
      },
    },
    imgCoverShowcase: {
      width: 146.33,
      height: 100,
      border: `1px solid ${theme.defaultTheme.gray_palette.light2}`,
      marginRight: 14,
      cursor: 'pointer',
      '@media (max-width: 600px)': {
        display: 'none',
      },
      '& > img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      },
    },
    titleCard: {
      display: 'flex',
      alignItems: 'center',
      '& > p': {
        fontSize: 16,
      },
    },
    message: {
      maxWidth: 190,
      '@media (max-width: 600px)': {
        maxWidth: 140,
      },
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    label: {
      color: theme.defaultTheme.main_palette.main,
      whiteSpace: 'nowrap',
    },
    access: {
      display: 'flex',
      marginTop: 'auto',
      marginBottom: 'initial !important',
      alignItems: 'center',
      '& > svg': {
        width: 14,
        height: 14,
        marginLeft: 6,
        fill: theme.defaultTheme.gray_palette.dark1,
      },
    },
    infos: {
      marginRight: 'auto',
      display: 'flex',
      flexDirection: 'column',
      cursor: 'pointer',
      '& > *': {
        marginBottom: 5,
      },
    },
    subtitle: {
      color: theme.defaultTheme.gray_palette.normal2,
    },
    time: {
      fontSize: 10,
    },
    dot: {
      width: 8,
      fill: theme.defaultTheme.support_palette.green,
      '@media (max-width: 600px)': {
        marginRight: 8,
      },
    },
    containerOptions: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
    },
    moreIcon: {
      marginTop: 'auto',
    },
    dotAndDate: {
      alignItems: 'center',
      display: 'flex',
      textTransform: 'capitalize',
      flexDirection: 'column',
      '@media (max-width: 600px)': {
        flexDirection: 'row-reverse',
      },
    },
    ellipsisContent: {
      overflow: 'hidden',
      maxWidth: 280,
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
  };
});

const UpdateItem = ({ update }: IProps) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const dispatch = useAppDispatch();
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const goToNotification = () => {
    dispatch(readAndUnreadUpdate({ id: update.id, read: true }));
    window.open(update.link);
  };

  const markAsReadAndUnread = () => {
    setAnchorEl(null);
    dispatch(readAndUnreadUpdate({ id: update.id, read: !update.read }));
  };
  const removeUpdate = () => {
    setAnchorEl(null);
    dispatch(deleteUpdate({ id: update.id }));
  };

  const getLabel = {
    content: () => 'Novo conteúdo',
    comment: () => 'Nova resposta',
    lesson: () => 'Nova aula',
    module: () => 'Novo módulo',
    newcourse: () => 'Novo curso',
    newcoursetrack: () => 'Novo curso disponível',
  };

  const renderCoverImage = () => {
    const defaultImage = require(`assets/img/nutror-card-${update.course_id % 5}.png`);
    const imageSrc = getCdnUrl(update.new_cover_url || update.cover_url, defaultImage);
    return (
      <div onClick={goToNotification} className={classes.imgCoverShowcase}>
        <img alt='imagem-capa' src={imageSrc} />
      </div>
    );
  };

  return (
    <div className={classes.container}>
      {renderCoverImage()}
      <div onClick={goToNotification} className={classes.infos}>
        <div className={classes.titleCard}>
          <Typography className={classes.label}>
            {getLabel[update.payload.type] && getLabel[update.payload.type]()}
          </Typography>
          <Typography className={classes.message}>
            {update.message && <Fragment>:&nbsp;{update.message}</Fragment>}
          </Typography>
        </div>
        {!!update.course_title && (
          <Typography className={classes.ellipsisContent} variant='body1'>
            {update.course_title}
          </Typography>
        )}
        {!!update.subtitle && (
          <Typography className={`${classes.subtitle} ${classes.ellipsisContent}`}>{update.subtitle}</Typography>
        )}
        <div className={classes.access}>
          <Typography className={classes.label} variant='body2'>
            Acessar
          </Typography>{' '}
          <ArrowForward />
        </div>
      </div>
      <div className={classes.containerOptions}>
        <div className={classes.dotAndDate}>
          <Typography className={classes.time} variant='body1'>
            {dataToNotifications(update.created_at)}
          </Typography>
          {!update.read && <FiberManualRecordIcon className={classes.dot} />}
        </div>
        <IconButton onClick={handleClick} className={classes.moreIcon}>
          <MoreVertIcon />
        </IconButton>

        <Menu
          id='long-menu'
          anchorEl={anchorEl}
          keepMounted
          open={open}
          onClose={handleClose}
          PaperProps={{
            style: {
              width: '25ch',
            },
          }}
        >
          <MenuItem onClick={removeUpdate}>
            <DeleteIcon />
            &nbsp; Excluir
          </MenuItem>
          <MenuItem onClick={markAsReadAndUnread}>
            {!update.read ? <VisibilityIcon /> : <VisibilityOffIcon />}&nbsp;{' '}
            {!update.read ? 'Marcar como lido' : 'Marcar como não lido'}
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

export default React.memo(UpdateItem);
